<script setup lang="ts">
import { reactive, ref } from 'vue'
import { uuid } from 'vue3-uuid'
import { APIClient } from '@/onenetwork/core/onenetwork.api.instant'
import EstateProjectAPI from '@/modules/estate-project/constants/estate-project-api-uri-const'
import ImagePicker from '@/onenetwork/components/image_picker/ImagePicker.vue'

const emit = defineEmits(['created'])

const loading = ref(false)
const logoUuid = ref(uuid.v4().toString())

const form = reactive({
  name: '',
  logo: '',
  description: '',
})

const handleChooseLogo = (payload: any) => {
  const media = payload?.response?.data || payload
  const url = media?.url?.original || media?.url?.medium || media?.url?.thumbnail || ''
  form.logo = url
  logoUuid.value = uuid.v4().toString()
}

const handleRemoveLogo = () => {
  form.logo = ''
  logoUuid.value = uuid.v4().toString()
}

const toRecord = (value: unknown): Record<string, unknown> => {
  if (typeof value === 'object' && value !== null) return value as Record<string, unknown>
  return {}
}

const normalizeOwner = (payload: unknown) => {
  const safePayload = toRecord(payload)
  const root = toRecord(safePayload.data)
  const nested = toRecord(root.data)

  const source = nested.id ? nested : root.id ? root : safePayload

  return {
    id: String(source.id || ''),
    name: String(source.name || source.title || ''),
    logo: source.logo,
    description: source.description,
  }
}

const handleSubmit = async () => {
  if (!form.name.trim()) return

  try {
    loading.value = true
    const res = await APIClient.post(EstateProjectAPI.investor.create, {
      name: form.name.trim(),
      logo: form.logo,
      description: form.description,
    })
    const investor = normalizeOwner(res)
    if (!investor.id || !investor.name) throw new Error('API chủ đầu tư trả dữ liệu không hợp lệ')
    emit('created', investor)
  } catch (e: any) {
    alert(e?.message || 'Không thể tạo chủ đầu tư')
  } finally {
    loading.value = false
  }
}
</script>

<template>
  <div class="investor-create-form">
    <div class="investor-create-field">
      <label>Tên chủ đầu tư <span>*</span></label>
      <input v-model="form.name" class="investor-input" placeholder="Công ty BĐS ABC" />
    </div>

    <div class="investor-create-field">
      <label>Logo <span>*</span></label>
      <div class="investor-logo-picker-wrap">
        <ImagePicker
          :uuid="logoUuid"
          type="photo"
          folder="real_estate"
          width="120px"
          height="100px"
          :src="form.logo"
          :previewSrc="form.logo"
          upload
          hideUploadPreview
          hideRemove
          previewIcon="sidekickicons:photo-plus-16-solid"
          @action:upload-success="handleChooseLogo"
          @action:choose-image="handleChooseLogo"
          @action:remove-image="handleRemoveLogo"
        />
        <div class="investor-logo-meta">
          <p>Thêm logo chủ đầu tư</p>
          <button type="button" class="investor-logo-remove" v-if="form.logo" @click="handleRemoveLogo">
            Gỡ logo
          </button>
        </div>
      </div>
    </div>

    <div class="investor-create-field">
      <label>Mô tả</label>
      <textarea
        v-model="form.description"
        class="investor-textarea"
        rows="4"
        placeholder="Mô tả chủ đầu tư..."
      ></textarea>
    </div>

    <button type="button" class="investor-submit-btn" :disabled="loading" @click="handleSubmit">
      {{ loading ? 'Đang tạo...' : 'Tạo chủ đầu tư' }}
    </button>
  </div>
</template>

<style scoped>
.investor-create-form {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.investor-create-field {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.investor-create-field label {
  font-size: 13px;
  font-weight: 700;
  color: #111827;
}

.investor-create-field label span {
  color: #ff0000;
}

.investor-input,
.investor-textarea {
  width: 100%;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  background: #f9fafb;
  padding: 10px 12px;
  font-size: 13px;
  color: #111827;
  outline: none;
}

.investor-textarea {
  resize: vertical;
  min-height: 90px;
}

.investor-input:focus,
.investor-textarea:focus {
  border-color: #0035ba;
  box-shadow: 0 0 0 2px rgba(0, 53, 186, 0.12);
}

.investor-logo-picker-wrap {
  display: flex;
  gap: 12px;
  align-items: center;
}

.investor-logo-meta p {
  margin: 0;
  font-size: 12px;
  color: #6b7280;
}

.investor-logo-remove {
  margin-top: 8px;
  border: 0;
  background: transparent;
  color: #dc2626;
  font-size: 12px;
  padding: 0;
}

.investor-submit-btn {
  width: 100%;
  border: 0;
  border-radius: 6px;
  background: #0035ba;
  color: #fff;
  font-size: 13px;
  font-weight: 700;
  padding: 10px 14px;
}

.investor-submit-btn:disabled {
  opacity: 0.6;
}
</style>
